import React from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { Boxes, CheckCircle2, Truck, AlertTriangle, Users, Wrench, Laptop, Monitor, Loader2 } from "lucide-react";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const StatCard = ({ icon: Icon, label, value, accent, testid, delay }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay }}>
    <Card data-testid={testid} className="p-6 border-slate-200 shadow-none rounded-md hover:border-slate-300 transition-colors duration-150">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p className="mt-2 font-heading font-extrabold text-3xl text-[#0F172A] tabular-nums">{value}</p>
        </div>
        <div className={`h-11 w-11 rounded-md flex items-center justify-center ${accent}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  </motion.div>
);

export default function Dashboard() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ["stats"],
    queryFn: async () => (await api.get("/dashboard/stats")).data,
  });
  const { data: rentals = [] } = useQuery({
    queryKey: ["rentals"],
    queryFn: async () => (await api.get("/rentals")).data,
  });

  if (isLoading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#0A2540]" /></div>;
  }

  const recent = rentals.slice(0, 6);

  return (
    <div data-testid="dashboard-page">
      <div className="mb-7">
        <h1 className="font-heading font-extrabold text-3xl tracking-tight text-[#0F172A]">Dashboard</h1>
        <p className="text-slate-500 mt-1">Overview of your rental fleet and active deliveries.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <StatCard testid="stat-total" delay={0} icon={Boxes} label="Total Units" value={stats.total_units} accent="bg-slate-100 text-[#0A2540]" />
        <StatCard testid="stat-available" delay={0.05} icon={CheckCircle2} label="Available" value={stats.available} accent="bg-emerald-100 text-emerald-600" />
        <StatCard testid="stat-rented" delay={0.1} icon={Truck} label="Rented Out" value={stats.rented_out} accent="bg-amber-100 text-amber-600" />
        <StatCard testid="stat-overdue" delay={0.15} icon={AlertTriangle} label="Overdue Returns" value={stats.overdue} accent="bg-red-100 text-red-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mt-6">
        <StatCard testid="stat-repair" icon={Wrench} label="Under Repair" value={stats.under_repair} accent="bg-indigo-100 text-indigo-600" />
        <StatCard testid="stat-clients" icon={Users} label="Clients" value={stats.total_clients} accent="bg-slate-100 text-[#0A2540]" />
        <Card className="p-6 border-slate-200 shadow-none rounded-md">
          <p className="text-sm font-medium text-slate-500 mb-4">Fleet Composition</p>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-slate-700"><Laptop className="h-4 w-4 text-[#0A2540]" /> Laptops</span>
              <span className="font-mono font-semibold">{stats.laptops}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-slate-700"><Monitor className="h-4 w-4 text-[#0A2540]" /> Desktops</span>
              <span className="font-mono font-semibold">{stats.desktops}</span>
            </div>
          </div>
        </Card>
      </div>

      <Card className="mt-6 border-slate-200 shadow-none rounded-md overflow-hidden" data-testid="recent-rentals">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-heading font-bold text-lg text-[#0F172A]">Recent Deliveries</h2>
          <span className="text-sm text-slate-500">{stats.active_rentals} active</span>
        </div>
        {recent.length === 0 ? (
          <p className="px-6 py-10 text-center text-slate-500">No deliveries recorded yet.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {recent.map((r) => (
              <div key={r.id} className="px-6 py-3.5 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-medium text-[#0F172A] truncate">{r.client_name}</p>
                  <p className="text-sm text-slate-500 font-mono">{r.asset_tag}</p>
                </div>
                <div className="text-right text-sm text-slate-500 hidden sm:block">
                  <p>Due {fmtDate(r.expected_return_date)}</p>
                </div>
                <StatusBadge status={r.status} />
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
